import { getNodeType, InputRule, isNodeActive, mergeAttributes } from '@tiptap/core';
import type { DOMOutputSpec } from '@tiptap/pm/model';
import { Node_Type } from 'ricos-schema';
import orderedListDataDefaults from 'ricos-schema/dist/statics/ordered_list.defaults.json';
import { OrderedListNodeChildrenPattern } from 'ricos-schema/next';
import { addDefaultsInCorrectFormat } from 'wix-rich-content-common';
import { createRicosNodeExtension, withBreakoutContainer } from 'wix-rich-content-plugin-commons';

import { createListInputRuleHandler } from '../list-input-rule-handler';
import styles from '../statics/styles.scss';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    orderedList: {
      /**
       * Toggle an ordered list
       */
      toggleOrderedList: () => ReturnType;
      /**
       * Toggle off an ordered list if exists
       */
      toggleOffOrderedList: () => ReturnType;
    };
  }
}

export const inputRegex = /^(\d+)\.\s$/;

export const orderedList = createRicosNodeExtension<{
  itemTypeName: string;
  HTMLAttributes: Record<string, string>;
}>({
  groups: ['no-trailing-node'],
  type: 'html-node',
  name: Node_Type.ORDERED_LIST,
  createExtensionConfig() {
    return {
      name: this.name,
      addOptions() {
        return {
          itemTypeName: Node_Type.LIST_ITEM,
          HTMLAttributes: { class: styles.orderedList },
        };
      },

      group: 'block list',

      content() {
        return OrderedListNodeChildrenPattern;
      },

      addAttributes() {
        return addDefaultsInCorrectFormat(orderedListDataDefaults);
      },

      parseHTML() {
        return [
          {
            tag: 'ol',
          },
        ];
      },

      renderHTML({ HTMLAttributes }) {
        return withBreakoutContainer([
          'ol',
          mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
          0,
        ]) as DOMOutputSpec;
      },

      addCommands() {
        return {
          toggleOrderedList:
            () =>
            ({ commands }) => {
              return commands.toggleLists(this.name, this.options.itemTypeName);
            },
          toggleOffOrderedList:
            () =>
            ({ state, commands }) => {
              const isActive = isNodeActive(state, getNodeType(this.name, state.schema));
              if (isActive) {
                return commands.toggleOrderedList();
              }
              return true;
            },
        };
      },

      addKeyboardShortcuts() {
        return {
          'Mod-Shift-7': () => this.editor.commands.toggleOrderedList(),
        };
      },

      addInputRules() {
        const getAttributes = match => ({ start: Number(match[1]) });
        const joinPredicate = (match, node) =>
          node.childCount + node.attrs.start === Number(match[1]);

        return [
          new InputRule({
            find: inputRegex,
            handler: createListInputRuleHandler({ type: this.type, getAttributes, joinPredicate }),
          }),
        ];
      },
    };
  },
});
